import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import CheckoutItem from '../../components/checkout-item/checkout-item.component';
import './checkout-confirmation.styles.jsx';
import { CheckoutContainer, OrderInfo, OrderInfoIcon, OrderInfoTextLarge, OrderInfoTextSmall, PaymentButtonContainer, PickupDetailsContainer, PickupDetailsItem, BoxError, Title } from './checkout-confirmation.styles.jsx';
import { BUTTON_TYPE_CLASSES } from '../../components/button/button.component';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import CheckoutDetails from '../../components/checkout-details/checkout-details.component';
import { selectOrderIsError } from '../../store/order/order.selector';
import { TextLarge, TextSmall, TotalContainer, TotalItem, TotalTextLg, TotalTextSm } from '../checkout/checkout.styles';
import { errorColor, successColor } from '../../constants';
import moment from 'moment';
import { formatScheduledPickupTime, getColor, getFontName } from '../../utils/utils.common';
import { formatDeliveryAddress } from '../../utils/deliveryAddress';
import { Box, useMediaQuery } from '@mui/material';
import { selectOnlineOrdersConfigs } from '../../store/kitchen/kitchen.selector';
import { ErrorIcon, SuccessIcon } from '../../components/icons';
import PaymentButton from '../../components/payment-button/payment-button.component';
import { CheckoutItemContainer, HeaderItem, HeaderText, PriceText } from '../../components/checkout-item/checkout-item.styles';
import { getOrderConfirmation } from '../../utils/firebase/firebase.utils';
import { setCustomerAccountCache, setPaymentProcessing } from '../../store/cart/cart.action';
import Spinner from '../../components/spinner/spinner.component';
import {
    PaymentContainer,
    SpinnerOverlay,
    SubmittingText,
    SubmittingSubtext,
} from '../payment/payment-form.styles';

const CheckoutConfirmation = () => {
    const location = useLocation();
    const { order: orderFromState } = location.state || {};
    const dispatch = useDispatch();

    const [confirmedOrder, setConfirmedOrder] = useState(null);
    const [confirmationFailed, setConfirmationFailed] = useState(false);
    const [isConfirming, setIsConfirming] = useState(true);

    const order = confirmedOrder
      ? { ...orderFromState, ...confirmedOrder }
      : orderFromState;

    const isError = useSelector(selectOrderIsError);
    const onlineOrdersConfig = useSelector(selectOnlineOrdersConfigs);
    const navigate = useNavigate();

    const headerModalBackground = getColor("header-modal-background", onlineOrdersConfig);
    const brandAccent = getColor("brand-accent", onlineOrdersConfig);
    const brandFontStyle = getFontName("brand-font-style", onlineOrdersConfig);
    const backgroundTertiary = getColor("background-tertiary", onlineOrdersConfig);
    const backgroundSecondary = getColor("background-secondary", onlineOrdersConfig);
    const brandPrimary = getColor("brand-primary", onlineOrdersConfig);

    const { kitchenSlug } = useParams();
    const isMobile = useMediaQuery('(max-width: 768px)');

    const firestoreOrderId = orderFromState?.orderId;
    const tyroPayRequestId = orderFromState?.tyroPayRequestId;
    const kitchenId = orderFromState?.kitchenId;
    const isConfirmed =
      order?.paymentStatus === "paid" && Boolean(order?.orderCount);

    useEffect(() => {
      if (isConfirmed) {
        setIsConfirming(false);
        return undefined;
      }

      if ((!firestoreOrderId && !tyroPayRequestId) || !kitchenId) {
        setConfirmationFailed(true);
        setIsConfirming(false);
        return undefined;
      }

      let cancelled = false;

      const loadConfirmation = async () => {
        setIsConfirming(true);
        try {
          const data = await getOrderConfirmation({
            orderId: firestoreOrderId,
            tyroPayRequestId,
            kitchenId,
          });
          if (cancelled) return;

          setConfirmedOrder({
            orderCount: data.orderCount,
            paymentStatus: data.paymentStatus,
            orderStatus: data.orderStatus,
            orderPayedAt: data.orderPayedAt,
            customerAccountId: data.customerAccountId,
            customerAccountEmail: data.customerAccountEmail,
            customerAccountMobileNumber: data.customerAccountMobileNumber,
          });

          if (data.customerAccountId && data.kitchenId) {
            const emailNormalized = (
              data.customerAccountEmail ||
              orderFromState?.customerAccountEmail ||
              ""
            )
              .trim()
              .toLowerCase();
            const mobileNormalized = (
              data.customerAccountMobileNumber ||
              orderFromState?.customerAccountMobileNumber ||
              ""
            ).replace(/\D/g, "");
            const identityKey = `${emailNormalized}|${mobileNormalized}`;
            if (emailNormalized || mobileNormalized) {
              dispatch(
                setCustomerAccountCache({
                  customerAccountId: data.customerAccountId,
                  kitchenId: data.kitchenId,
                  identityKey,
                })
              );
            }
          }
        } catch (error) {
          console.error("Order confirmation failed:", error);
          if (!cancelled) {
            setConfirmationFailed(true);
          }
        } finally {
          if (!cancelled) {
            setIsConfirming(false);
          }
        }
      };

      loadConfirmation();

      return () => {
        cancelled = true;
      };
    }, [
      dispatch,
      firestoreOrderId,
      tyroPayRequestId,
      kitchenId,
      isConfirmed,
      orderFromState?.customerAccountEmail,
      orderFromState?.customerAccountMobileNumber,
    ]);

    const showConfirmationError =
      isError instanceof Error ||
      !orderFromState ||
      (confirmationFailed && !isConfirmed);
    const isShowingConfirmingLoader =
      !showConfirmationError && (isConfirming || !isConfirmed);

    useEffect(() => {
      dispatch(setPaymentProcessing(isShowingConfirmingLoader));
      return () => {
        dispatch(setPaymentProcessing(false));
      };
    }, [dispatch, isShowingConfirmingLoader]);

    const getDiscountItem = order?.cartItems?.find((item) => item.categoryId === "custom-discounts");
    const getCartItems = order?.cartItems?.filter(item => item.categoryId !== "custom-discounts")
    const groupedCartItems = useMemo(() => {
      if (!getCartItems || getCartItems.length === 0) return [];

      const groups = [];
      const processedChildren = new Set();
      const bundleGroupMap = new Map();

      const serializeVariants = (variants = []) =>
        variants
          .map(
            (variant) =>
              `${variant?.optionCategory || ""}:${variant?.name || ""}:${
                Number(variant?.charge) || 0
              }:${variant?.extraCharge ? 1 : 0}`
          )
          .sort()
          .join("|");

      const buildBundleGroupKey = (parent, children) => {
        const orderedChildren = [...children].sort((a, b) =>
          `${a?.bundleStepId || ""}-${a?.dishName || ""}`.localeCompare(
            `${b?.bundleStepId || ""}-${b?.dishName || ""}`
          )
        );

        const childSignature = orderedChildren
          .map(
            (child) =>
              `${child?.bundleStepId || ""}:${child?.id || ""}:${
                child?.dishName || ""
              }:${Number(child?.priceAdjustment) || 0}:${child?.note || ""}:${serializeVariants(
                child?.variantSelected || []
              )}`
          )
          .join("||");

        return `${parent?.bundleTemplateId || parent?.id || parent?.dishName || ""}::${childSignature}`;
      };

      getCartItems.forEach((item) => {
        if (item.bundleParentId) return;

        if (item.isBundle === true) {
          const children = getCartItems.filter(
            (child) => child.bundleParentId === item.cartItemId
          );
          children.forEach((child) => processedChildren.add(child.cartItemId));
          const bundleGroupKey = buildBundleGroupKey(item, children);
          const existingBundleGroup = bundleGroupMap.get(bundleGroupKey);

          if (existingBundleGroup) {
            existingBundleGroup.bundleAggregate.parentIds.push(item.cartItemId);
            existingBundleGroup.bundleAggregate.instanceCount +=
              Number(item.quantity) || 1;
            existingBundleGroup.bundleAggregate.totalPrice +=
              Number(item.dishPriceWithVariants || 0) * Number(item.quantity || 1);
            return;
          }

          const newBundleGroup = {
            type: "bundle",
            parent: item,
            children,
            bundleAggregate: {
              parentIds: [item.cartItemId],
              instanceCount: Number(item.quantity) || 1,
              totalPrice:
                Number(item.dishPriceWithVariants || 0) * Number(item.quantity || 1),
            },
          };

          bundleGroupMap.set(bundleGroupKey, newBundleGroup);
          groups.push(newBundleGroup);
          return;
        }

        groups.push({ type: "regular", item });
      });

      getCartItems.forEach((item) => {
        if (item.bundleParentId && !processedChildren.has(item.cartItemId)) {
          groups.push({ type: "regular", item });
        }
      });

      return groups;
    }, [getCartItems]);

    const getOrderType = order?.orderType ? order.orderType : null;
    const getTableNumber = order?.tableNumber ? order.tableNumber : null;
    const getDeliveryAddress = formatDeliveryAddress(order?.deliveryAddress) || null;
    const deliveryFee = order?.deliveryFee ? order.deliveryFee : 0;
    const getScheduledTime = formatScheduledPickupTime(order?.scheduledPickupTime)
    const getScheduledDeliveryTime = formatScheduledPickupTime(
      order?.scheduledDeliveryTime
    );

        const cartNotDiscountTotal =
          getCartItems?.reduce(
            (total, cartItem) =>
              total + cartItem.quantity * cartItem.dishPriceWithVariants,
            0
          ) ?? null;

  const orderSubtotal = (() => {
    if (typeof order?.cartSubtotal === "number") {
      return Number(order.cartSubtotal);
    }

    if (!getCartItems) return null;

    const subtotal = getCartItems.reduce(
      (total, cartItem) =>
        total + cartItem.quantity * cartItem.dishPriceWithVariants,
      0
    );

    if (getDiscountItem?.discountType === "percentage") {
      return subtotal - (subtotal * getDiscountItem.discountValue) / 100;
    }

    if (getDiscountItem?.discountType === "amount") {
      return subtotal - getDiscountItem.discountValue;
    }

    return subtotal;
  })();

    const orderTotal = order?.totalAmount ? order.totalAmount : 0;
    const holidaySurcharge = order?.holidaySurcharge ? order.holidaySurcharge : 0;
    const activeSurcharge = order?.surcharge;
    const cardTotalFee = order?.cardSurcharge ? order.cardSurcharge : 0;

    const shortOrderId = order?.orderId?.slice(-8).toUpperCase() ? order.orderId.slice(-8).toUpperCase() : null

    const userFriendlyId = order?.orderCount ? order.orderCount : shortOrderId;
    const orderWaitTime = order?.orderWaitTime ? order.orderWaitTime : 15;
    const kitchenAddress = order?.kitchenAddress ? order.kitchenAddress : "";
    const kitchenName = order?.kitchenName ? order.kitchenName : "";

    const customerEmail =
      order?.customerDetails?.customerEmail ||
      order?.customerAccountEmail ||
      null;

    let time;
    if (order?.createdAt) {

        time = moment(
            order?.createdAt
        ).add(orderWaitTime, "minute").format('hh:mm A');
    }

    // order not found if session id is wrong or not stored in DB
    if (showConfirmationError) {
        return (
            <Box sx={{ backgroundColor: headerModalBackground, minHeight: 'calc(100vh - 72px)', "@media screen and (max-width: 768px)": { minHeight: 'calc(100vh - 64px)' }, }}>
                <CheckoutContainer>
                    {!isMobile && (
                        <Title brandFontStyle={brandFontStyle} brandAccent={brandAccent}>Order Confirmation</Title>
                    )}
                    <OrderInfo backgroundColor={errorColor}>
                        <OrderInfoIcon>
                            <ErrorIcon />
                        </OrderInfoIcon>
                        <OrderInfoTextLarge>
                            Oops, something went wrong.
                        </OrderInfoTextLarge>
                        <OrderInfoTextSmall>
                            Try Refresh the page and will try find your order. 
                        </OrderInfoTextSmall>
                    </OrderInfo>
                    <BoxError>
                        <div style={{ marginBottom: '10px' }}>
                            If you don't receive a confirmation email, ask at the counter or call the venue to see if your order has gone through.
                        </div>
                        <div>
                            If you receive a confirmation email, this means your order request has been sent.
                        </div>
                    </BoxError>
                    <div style={{ margin: '0px 16px' }}>
                        <PaymentButton
                            buttonType={BUTTON_TYPE_CLASSES.cart}
                            onClick={() => window.location.reload(true)}
                            style={{ maxWidth: '100%' }}
                            text="Refresh"
                        />
                    </div>

                </CheckoutContainer>
            </Box>
        )
    }

    if (isConfirming || !isConfirmed) {
      // Match payment processing layout exactly (copy differs only).
      return (
        <Box
          sx={{
            backgroundColor: headerModalBackground,
            minHeight: "calc(100vh - 72px)",
            "@media screen and (max-width: 768px)": {
              minHeight: "calc(100vh - 64px)",
            },
          }}
        >
          <PaymentContainer>
            <SpinnerOverlay backgroundSecondary={backgroundSecondary}>
              <Spinner size={64} color={brandPrimary} />
              <SubmittingText>Confirming Order</SubmittingText>
              <SubmittingSubtext>Don&apos;t close the page</SubmittingSubtext>
            </SpinnerOverlay>
          </PaymentContainer>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          backgroundColor: headerModalBackground,
          minHeight: "calc(100vh - 72px)",
          "@media screen and (max-width: 768px)": {
            minHeight: "calc(100vh - 64px)",
          },
        }}
      >
        <CheckoutContainer>
          {!isMobile && (
            <Title brandFontStyle={brandFontStyle} brandAccent={brandAccent}>
              Order Confirmation
            </Title>
          )}
          <OrderInfo backgroundColor={successColor}>
            <OrderInfoIcon>
              <SuccessIcon />
            </OrderInfoIcon>
            <OrderInfoTextLarge>Order # {userFriendlyId}</OrderInfoTextLarge>
            <OrderInfoTextSmall>
              Confirmation has been sent to your email {customerEmail}
            </OrderInfoTextSmall>
          </OrderInfo>
          <PickupDetailsContainer backgroundTertiary={backgroundTertiary}>
            <PickupDetailsItem>
              {getOrderType === "Take Away" ? (
                <CheckoutDetails
                  title="Take Away"
                  body={`${
                    getScheduledTime === null
                      ? `Pick up ASAP in ${orderWaitTime} mins`
                      : `Pick Up ${getScheduledTime}`
                  }`}
                />
              ) : getOrderType === "Delivery" ? (
                <CheckoutDetails
                  title="Delivery"
                  body={
                    <>
                      <div>
                        {getScheduledDeliveryTime === null
                          ? `Deliver ASAP in ${orderWaitTime} mins`
                          : `Deliver ${getScheduledDeliveryTime}`}
                      </div>
                      <div>{getDeliveryAddress || "Delivery address"}</div>
                    </>
                  }
                />
              ) : (
                <CheckoutDetails
                  title="Dine In"
                  body={`Table ${getTableNumber}`}
                />
              )}
            </PickupDetailsItem>
            <PickupDetailsItem>
              <div>
                <TextLarge>{kitchenName}</TextLarge>
                <TextSmall>{kitchenAddress}</TextSmall>
              </div>
            </PickupDetailsItem>
          </PickupDetailsContainer>
          {groupedCartItems?.map((group) =>
            group.type === "bundle" ? (
              <CheckoutItem
                key={group.parent.cartItemId}
                cartItem={group.parent}
                bundleChildren={group.children}
                bundleAggregate={group.bundleAggregate}
              />
            ) : (
              <CheckoutItem key={group.item.cartItemId} cartItem={group.item} />
            )
          )}

         {getDiscountItem?.discountType && getDiscountItem?.discountReason && getDiscountItem?.discountValue && (
            <CheckoutItemContainer backgroundTertiary={backgroundTertiary}>
              <HeaderItem>
                <HeaderText>
                  Discount{" "}
                  {getDiscountItem.discountType === "amount" ? "$" : ""}
                  {getDiscountItem.discountValue}
                  {getDiscountItem.discountType === "percentage" ? "%" : ""}{" "}
                  Off
                </HeaderText>

                <PriceText>
                  $
                  {getDiscountItem.discountType === "percentage"
                    ? Number(
                        (cartNotDiscountTotal * getDiscountItem.discountValue) /
                        100
                      )?.toFixed(2)
                    : getDiscountItem.discountType === "amount"
                    ?  Number(getDiscountItem.discountValue)?.toFixed(2)
                    : "0.00"}
                </PriceText>
              </HeaderItem>

              <TotalItem>
                <TotalTextSm style={{ marginBottom: 0 }}>
                  Code: {getDiscountItem?.discountReason || "N/A"}
                </TotalTextSm>
              </TotalItem>
            </CheckoutItemContainer>
          )}
          <TotalContainer>
            <TotalItem>
              <TotalTextSm>Subtotal</TotalTextSm>
              <TotalTextSm>${orderSubtotal?.toFixed(2)}</TotalTextSm>
            </TotalItem>
            {activeSurcharge?.enabled && holidaySurcharge !== 0 && (
              <TotalItem>
                <TotalTextSm>{activeSurcharge?.name} Surcharge</TotalTextSm>
                <TotalTextSm>${Number(holidaySurcharge)?.toFixed(2)}</TotalTextSm>
              </TotalItem>
            )}
            {deliveryFee !== 0 && (
              <TotalItem>
                <TotalTextSm>Delivery Fee</TotalTextSm>
                <TotalTextSm>${Number(deliveryFee)?.toFixed(2)}</TotalTextSm>
              </TotalItem>
            )}
            {cardTotalFee !== 0 && (
              <TotalItem>
                <TotalTextSm>Card Surcharge</TotalTextSm>
                <TotalTextSm>${Number(cardTotalFee)?.toFixed(2)}</TotalTextSm>
              </TotalItem>
            )}
            <TotalItem>
              <TotalTextLg>Total (Including GST)</TotalTextLg>
              <TotalTextLg>${orderTotal?.toFixed(2)}</TotalTextLg>
            </TotalItem>
          </TotalContainer>
          {isMobile && (
            <PaymentButtonContainer
              headerModalBackground={headerModalBackground}
              backgroundTertiary={backgroundTertiary}
            >
              <PaymentButton
                buttonType={BUTTON_TYPE_CLASSES.cart}
                onClick={() => navigate(`/${kitchenSlug}`)}
                style={{ maxWidth: "100%" }}
                text="Back to menu"
              />
            </PaymentButtonContainer>
          )}
        </CheckoutContainer>
      </Box>
    );
}

export default CheckoutConfirmation;
